import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function WIOAProgram() {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-blue-900/90 to-blue-800/90 text-white py-32">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/bfa95f06c776a23b777b7844fe5b694f.jpg"
              alt="WIOA Program"
              className="w-full h-full object-cover object-top opacity-30"
            />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">WIOA Program</h1>
            <p className="text-xl md:text-2xl max-w-4xl mx-auto leading-relaxed">
              Discover how the Workforce Innovation and Opportunity Act can fund your path to a rewarding trade career with nationally recognized certifications—at no cost to you.
            </p>
          </div>
        </section>

        {/* Train at No Cost Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-blue-900 mb-6">
                  Launch Your Career Without Financial Barriers
                </h2>
                <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                  The Workforce Innovation and Opportunity Act (WIOA) is a federal initiative managed by the U.S. Department of Labor and Department of Education. Its mission is to remove employment obstacles for individuals seeking quality jobs by providing access to essential skills training and professional certifications.
                </p>
                <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                  WIOA empowers job seekers—whether entering the workforce for the first time or making a career transition—by covering training costs for in-demand occupations that lead to stable, long-term employment.
                </p>
                <a 
                  href="#contact" 
                  className="inline-block bg-yellow-400 text-blue-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Get Started Today
                </a>
              </div>
              <div className="relative">
                <img 
                  src="https://readdy.ai/api/search-image?query=mature%20adult%20student%20in%20his%2040s%20wearing%20safety%20vest%20and%20hard%20hat%2C%20smiling%20confidently%20in%20technical%20training%20facility%2C%20professional%20trade%20school%20environment%2C%20warm%20natural%20lighting%2C%20realistic%20photography%20style%2C%20diverse%20workforce%20representation&width=800&height=900&seq=wioa-student-1&orientation=portrait"
                  alt="Student Success"
                  className="rounded-lg shadow-2xl w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* WIOA Funding Details Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1">
                <img 
                  src="https://readdy.ai/api/search-image?query=young%20female%20electrician%20in%20her%2020s%20wearing%20safety%20glasses%20and%20work%20uniform%20diagnosing%20electrical%20problem%20with%20outlet%20on%20wall%20in%20residential%20home%20interior%20using%20multimeter%20and%20electrical%20testing%20equipment%20focused%20professional%20expression%20realistic%20photography%20style%20modern%20home%20setting%20bright%20natural%20lighting%20skilled%20female%20tradesperson%20representation&width=800&height=900&seq=wioa-female-electrician-outlet&orientation=portrait"
                  alt="WIOA Funding"
                  className="rounded-lg shadow-2xl w-full h-auto object-cover object-top"
                />
              </div>
              <div className="order-1 md:order-2">
                <h2 className="text-4xl font-bold text-blue-900 mb-8">Understanding WIOA Funding</h2>
                <div className="space-y-6">
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold text-blue-900 mb-3">Who Provides WIOA Grants?</h3>
                    <p className="text-gray-700">
                      Funding comes from the U.S. Department of Labor working in partnership with the Department of Education to support workforce development nationwide.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold text-blue-900 mb-3">How Do I Check My Eligibility?</h3>
                    <p className="text-gray-700">
                      American Job Centers (AJC) administer WIOA funding locally. Contact your nearest AJC to verify your qualification status and begin the application process.{' '}
                      <a 
                        href="https://www.twc.texas.gov/find-locations" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                      >
                        https://www.twc.texas.gov/find-locations
                      </a>
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold text-blue-900 mb-3">What's the Funding Amount?</h3>
                    <p className="text-gray-700">
                      Grant amounts vary based on your location, local AJC budget allocation, and individual circumstances. Your AJC advisor will provide specific details.
                    </p>
                  </div>
                  <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-xl font-bold text-blue-900 mb-3">When Can Training Begin?</h3>
                    <p className="text-gray-700">
                      Once approved and all required documentation is complete, we work to enroll you quickly. New courses start weekly to accommodate your schedule.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Eligibility Requirements Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold text-blue-900 mb-8">WIOA Eligibility Requirements</h2>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                    </div>
                    <p className="ml-4 text-lg text-gray-700">Must be at least 18 years old</p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                    </div>
                    <p className="ml-4 text-lg text-gray-700">U.S. citizenship required</p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                    </div>
                    <p className="ml-4 text-lg text-gray-700">High school diploma or GED equivalent</p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                    </div>
                    <p className="ml-4 text-lg text-gray-700">Eligible for government assistance programs</p>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                    </div>
                    <div className="ml-4">
                      <p className="text-lg text-gray-700 font-semibold">Dislocated worker status</p>
                      <p className="text-gray-600 mt-1">Examples: Factory closures, layoffs, homemakers who lost primary household income</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                    </div>
                    <div className="ml-4">
                      <p className="text-lg text-gray-700 font-semibold">Recently terminated or facing layoff</p>
                      <p className="text-gray-600 mt-1">Must be eligible for or have exhausted unemployment benefits, or work at a facility closing within 180 days</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                      <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                    </div>
                    <div className="ml-4">
                      <p className="text-lg text-gray-700 font-semibold">Self-employed but currently unemployed</p>
                      <p className="text-gray-600 mt-1">Includes farmers, ranchers, and fishermen affected by economic downturns or natural disasters</p>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=middle-aged%20male%20student%20in%20his%2050s%20with%20gray%20hair%2C%20wearing%20work%20uniform%20and%20holding%20technical%20manual%2C%20standing%20in%20professional%20training%20workshop%2C%20confident%20and%20motivated%20expression%2C%20realistic%20photography%20style%2C%20diverse%20age%20representation%20in%20workforce%20training&width=800&height=900&seq=wioa-student-3&orientation=portrait"
                  alt="Eligibility"
                  className="rounded-lg shadow-2xl w-full h-auto object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Career Opportunities Section */}
        <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800 text-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-8">
              Build a Brighter Future Through Career Training
            </h2>
            <div className="space-y-6 text-lg leading-relaxed">
              <p>
                WIOA is more than just funding—it's an investment in your future success.
              </p>
              <p>
                The program breaks the cycle of unemployment by equipping you with high-demand skills that lead to sustainable careers. That's why focusing on growing industries with strong job prospects is essential.
              </p>
              <p>
                Professional credentials and certifications offer a fast track to employment, typically requiring just 3 to 6 months of training followed by a certification exam. These stackable credentials allow you to continuously advance your skills and expand your career opportunities over time.
              </p>
            </div>
          </div>
        </section>

        {/* TWC Approval Section */}
        <section className="py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-blue-900 mb-8">
              Texas Workforce Academy is TWC Approved for WIOA Workforce Development
            </h2>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed max-w-3xl mx-auto">
              Texas Workforce Academy is recognized as an Eligible Training Provider (ETP) by the Texas Workforce Commission, authorized to deliver WIOA-funded training services to qualified Texas residents. Our experienced advisors are ready to answer all your questions about WIOA requirements and help you get started.
            </p>
            <a 
              href="#contact" 
              className="inline-block bg-yellow-400 text-blue-900 px-8 py-4 rounded-full font-bold text-lg hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Talk To An Advisor
            </a>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-blue-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started with WIOA?</h2>
            <p className="text-xl text-white mb-8">
              Contact us today to learn more about WIOA eligibility and how we can help fund your training.
            </p>
            <Button href="/contact" size="lg">
              Contact Us Today
            </Button>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 bg-blue-900 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Ready to Get Started?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-blue-800 p-8 rounded-lg">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-phone-fill text-5xl text-yellow-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;Call Us</h3>
                <p className="text-lg">&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; 940-241-7243</p>
              </div>
              <div className="bg-blue-800 p-8 rounded-lg">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-mail-fill text-5xl text-yellow-400"></i>
                </div>
                <h3 className="text-xl font-bold mb-2">&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; Email Us</h3>
                <p className="text-lg">&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;info@texasworkforceacademy.org</p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
